import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';

import SearchIcon from '@mui/icons-material/Search';
import TableRow from '@mui/material/TableRow';
import Table from 'components/Table';
import TableHeadCell from 'components/TableHeadCell';
import TableCell from 'components/TableCell';
import FabDialog from 'components/FabDialog';
import SeoRoutesListRow from 'components/SeoRoutesListRow';
import SeoRoutesListAdd from 'components/SeoRoutesListAdd';

import { useSearch } from 'components/Search';
import { usePagination } from 'components/Pagination';
import { useAvoidSuspense, useDelete, useSkipFirstLoad } from 'lib';

import Query, {
  SeoRoutesListRefetchQuery,
} from '__generated__/SeoRoutesListRefetchQuery.graphql';
import { SeoRoutesList_data$key } from '__generated__/SeoRoutesList_data.graphql';

type Props = {
  data: SeoRoutesList_data$key;
};

const SeoRoutesList: FC<Props> = props => {
  const { search } = useSearch();
  const { variables } = usePagination();

  const [{ seoRoutes }, { refetch, loading }] = useAvoidSuspense<
    SeoRoutesListRefetchQuery,
    SeoRoutesList_data$key
  >(
    graphql`
      fragment SeoRoutesList_data on Query
      @refetchable(queryName: "SeoRoutesListRefetchQuery")
      @argumentDefinitions(
        first: { type: "Int!" }
        after: { type: "String" }
        search: { type: "String" }
      ) {
        seoRoutes(first: $first, after: $after, search: $search) {
          __id
          edges {
            node {
              id

              ...SeoRoutesListRow_seoRoute
            }
          }

          pageInfo {
            ...Table_pageInfo
          }
        }
      }
    `,
    props.data,
    Query
  );

  const connectionId = seoRoutes?.__id;

  const [deleteSeoRoute, deleting] = useDelete(connectionId);

  const { edges, pageInfo } = seoRoutes ?? {};

  useSkipFirstLoad(
    useCallback(() => {
      refetch({
        ...variables,
        search,
      });
    }, [refetch, search, variables])
  );

  return (
    <>
      <Table
        search
        pagination
        pageInfo={pageInfo ?? null}
        icon={<SearchIcon />}
        loading={loading || deleting}
        head={
          <TableRow>
            <TableHeadCell name="name">Tipas</TableHeadCell>

            <TableHeadCell center name="sourceUrl">
              Ateinantis URL
            </TableHeadCell>

            <TableHeadCell center name="targetUrl">
              Redirect URL
            </TableHeadCell>

            <TableCell center>Paieškos parametrai</TableCell>

            <TableHeadCell center name="packageCount">
              Kelionių
            </TableHeadCell>

            <TableHeadCell center name="monthlySourceVisits">
              Ateinančio URL peržiūros / 28d
            </TableHeadCell>

            <TableHeadCell center name="monthlyDestinationVisits">
              Redirect URL peržiūros / 28d
            </TableHeadCell>

            <TableCell />
          </TableRow>
        }>
        {edges?.map(({ node }) => (
          <SeoRoutesListRow
            key={node.id}
            seoRoute={node}
            onDelete={deleteSeoRoute}
          />
        ))}
      </Table>

      {connectionId && (
        <FabDialog fullWidth maxWidth="sm">
          <SeoRoutesListAdd connectionId={connectionId} />
        </FabDialog>
      )}
    </>
  );
};

export default SeoRoutesList;
